import { useState, useEffect } from "react";
import { ListenerLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO, isPast, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar, Clock, Video, User, Send, ArrowUpDown, Mail, Timer, CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListenerSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMeetDialog, setShowMeetDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [meetLink, setMeetLink] = useState("");
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState("newest"); // newest, oldest, alphabetical
  
  // New suggestion dialog
  const [showSuggestDialog, setShowSuggestDialog] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [usersForSuggestion, setUsersForSuggestion] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [suggestionType, setSuggestionType] = useState("days"); // days, weeks, custom
  const [suggestionValue, setSuggestionValue] = useState(7);
  const [customDate, setCustomDate] = useState(null);
  const [customTime, setCustomTime] = useState("10:00");
  const [suggestionMessage, setSuggestionMessage] = useState("");
  
  // Suggestions sent list
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeTab, setActiveTab] = useState("sessions");

  useEffect(() => {
    fetchSessions();
    fetchSuggestions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/listener/sessions`);
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/listener/suggestions`);
      setSuggestions(response.data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoadingSuggestions(false);
    }
  };
  
  const fetchUsersForSuggestion = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/listener/users-for-suggestion`);
      setUsersForSuggestion(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const openSuggestDialog = () => {
    setShowSuggestDialog(true);
    setSelectedUserId("");
    setSuggestionType("days");
    setSuggestionValue(7);
    setCustomDate(null);
    setCustomTime("10:00");
    setSuggestionMessage("");
    fetchUsersForSuggestion();
  };
  
  const sendSuggestion = async () => {
    if (!selectedUserId) {
      toast.error("Selecciona un usuario");
      return;
    }
    
    if (suggestionType === "custom" && (!customDate || !customTime)) {
      toast.error("Selecciona fecha y hora");
      return;
    }
    
    setSuggestLoading(true);
    try {
      const payload = {
        user_id: selectedUserId,
        suggestion_type: suggestionType,
        value: suggestionValue,
        custom_date: customDate ? format(customDate, "yyyy-MM-dd") : null,
        custom_time: customTime,
        message: suggestionMessage || null
      };
      
      await axios.post(`${API}/listener/suggest-session`, payload);
      toast.success("Sugerencia enviada. El usuario recibirá un email.");
      setShowSuggestDialog(false);
      fetchSuggestions();
    } catch (error) {
      console.error("Error sending suggestion:", error);
      const msg = error.response?.data?.detail || "Error al enviar sugerencia";
      toast.error(msg);
    } finally {
      setSuggestLoading(false);
    }
  };

  const updateMeetLink = async () => {
    if (!meetLink || !selectedSession) return;
    
    try {
      await axios.put(`${API}/sessions/${selectedSession.id}/meet-link?meet_link=${encodeURIComponent(meetLink)}`);
      await fetchSessions();
      setShowMeetDialog(false);
      setMeetLink("");
      setSelectedSession(null);
      toast.success("Enlace de Google Meet actualizado");
    } catch (error) {
      console.error("Error updating meet link:", error);
      toast.error("Error al actualizar el enlace");
    }
  };

  // Sorting function
  const sortSessions = (sessionsList) => {
    const sorted = [...sessionsList];
    switch (sortOrder) {
      case "newest":
        return sorted.sort((a, b) => {
          const dateA = parseISO(`${a.date}T${a.time}`);
          const dateB = parseISO(`${b.date}T${b.time}`);
          return dateB - dateA;
        });
      case "oldest":
        return sorted.sort((a, b) => {
          const dateA = parseISO(`${a.date}T${a.time}`);
          const dateB = parseISO(`${b.date}T${b.time}`);
          return dateA - dateB;
        });
      case "alphabetical":
        return sorted.sort((a, b) => a.user_name.localeCompare(b.user_name));
      default:
        return sorted;
    }
  };

  const upcomingSessions = sortSessions(sessions.filter(s => {
    const sessionDate = parseISO(`${s.date}T${s.time}`);
    return !isPast(sessionDate) && s.status !== 'cancelled';
  }));

  const pastSessions = sortSessions(sessions.filter(s => {
    const sessionDate = parseISO(`${s.date}T${s.time}`);
    return isPast(sessionDate) || s.status === 'cancelled';
  }));
  
  // Get status badge for suggestions
  const getSuggestionStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700"><Timer className="w-3 h-3" />Pendiente</span>;
      case "paid":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle className="w-3 h-3" />Pagada</span>;
      case "expired":
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" />Expirada</span>;
      default:
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-600">{status}</span>;
    }
  };

  if (loading) {
    return (
      <ListenerLayout title="Sesiones">
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </ListenerLayout>
    );
  }

  return (
    <ListenerLayout title="Sesiones">
      <div className="space-y-6">
        {/* Header with sorting and suggest button */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2">
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[180px]" data-testid="sort-select">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Ordenar por" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Más recientes</SelectItem>
                <SelectItem value="oldest">Más antiguas</SelectItem>
                <SelectItem value="alphabetical">Alfabético (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={openSuggestDialog} className="btn-primary" data-testid="suggest-session-btn">
            <Send className="w-4 h-4 mr-2" />
            Sugerir sesión
          </Button>
        </div>
        
        {/* Tabs: Sessions / Suggestions Sent */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="sessions" data-testid="tab-sessions">Sesiones</TabsTrigger>
            <TabsTrigger value="suggestions" data-testid="tab-suggestions">
              Sugerencias enviadas
              {suggestions.filter(s => s.status === "pending").length > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-2">
                  {suggestions.filter(s => s.status === "pending").length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Sessions Tab */}
          <TabsContent value="sessions" className="space-y-8 mt-6">
            {/* Upcoming */}
            <section>
              <h2 className="text-lg font-semibold text-foreground mb-4">Próximas sesiones</h2>
              {upcomingSessions.length === 0 ? (
                <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
                  <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-600">No tienes sesiones programadas</p>
                  <Button variant="outline" className="mt-4" onClick={openSuggestDialog}>
                    <Send className="w-4 h-4 mr-2" />
                    Sugerir sesión a un usuario
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingSessions.map((session) => (
                    <div key={session.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`session-${session.id}`}>
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{session.user_name}</h3>
                            <p className="text-sm text-stone-500">{session.user_email}</p>
                            <div className="mt-2 flex items-center gap-4 text-sm text-stone-600 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(session.date), "EEEE d 'de' MMMM", { locale: es })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {session.time}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedSession(session);
                              setMeetLink(session.google_meet_link || "");
                              setShowMeetDialog(true);
                            }}
                            data-testid={`add-meet-link-${session.id}`}
                          >
                            <Video className="w-4 h-4 mr-1" />
                            {session.google_meet_link ? "Editar enlace" : "Añadir Meet"}
                          </Button>
                        </div>
                      </div>
                      {session.google_meet_link && (
                        <div className="mt-4 pt-4 border-t border-stone-100">
                          <a 
                            href={session.google_meet_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-primary text-sm hover:underline flex items-center gap-1"
                          >
                            <Video className="w-4 h-4" />
                            {session.google_meet_link}
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Past */}
            {pastSessions.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-foreground mb-4">Sesiones anteriores</h2>
                <div className="space-y-4">
                  {pastSessions.map((session) => (
                    <div key={session.id} className="bg-stone-50 rounded-2xl border border-stone-200 p-6" data-testid={`past-session-${session.id}`}>
                      <div className="flex items-start justify-between flex-wrap gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-stone-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="w-6 h-6 text-stone-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{session.user_name}</h3>
                            <div className="mt-1 flex items-center gap-4 text-sm text-stone-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(session.date), "d 'de' MMMM, yyyy", { locale: es })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {session.time}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </TabsContent>
          
          {/* Suggestions Tab */}
          <TabsContent value="suggestions" className="mt-6">
            {loadingSuggestions ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : suggestions.length === 0 ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
                <Mail className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-600">No has enviado sugerencias de sesión</p>
                <Button className="mt-4" onClick={openSuggestDialog}>
                  <Send className="w-4 h-4 mr-2" />
                  Sugerir primera sesión
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {suggestions.map((suggestion) => (
                  <div key={suggestion.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`suggestion-${suggestion.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-foreground">{suggestion.user_name}</h3>
                            {getSuggestionStatusBadge(suggestion.status)}
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-sm text-stone-600 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {format(parseISO(suggestion.suggested_date), "d 'de' MMMM, yyyy", { locale: es })}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {suggestion.suggested_time}
                            </span>
                          </div>
                          {suggestion.message && (
                            <p className="mt-2 text-sm text-stone-500 italic">"{suggestion.message}"</p>
                          )}
                          <p className="mt-1 text-xs text-stone-400">
                            Enviada {format(parseISO(suggestion.created_at), "d/MM/yyyy HH:mm", { locale: es })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Meet Link Dialog */}
      <Dialog open={showMeetDialog} onOpenChange={setShowMeetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enlace de Google Meet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Enlace de la videollamada
              </label>
              <Input
                type="url"
                value={meetLink}
                onChange={(e) => setMeetLink(e.target.value)}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                data-testid="meet-link-input"
              />
              <p className="mt-1 text-xs text-stone-500">
                Crea una reunión en Google Meet y pega el enlace aquí
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowMeetDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={updateMeetLink} className="btn-primary" data-testid="save-meet-link-btn">
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Suggest Session Dialog */}
      <Dialog open={showSuggestDialog} onOpenChange={setShowSuggestDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Sugerir sesión a un usuario</DialogTitle>
            <DialogDescription>
              El usuario recibirá un email con un enlace para pagar. La invitación expira en 8 horas.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* User selection */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Selecciona un usuario
              </label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-stone-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Cargando usuarios...
                </div>
              ) : usersForSuggestion.length === 0 ? (
                <p className="text-sm text-stone-500">
                  No tienes usuarios con sesiones anteriores. Los usuarios aparecerán aquí después de su primera sesión contigo.
                </p>
              ) : (
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger data-testid="user-select">
                    <SelectValue placeholder="Selecciona un usuario" />
                  </SelectTrigger>
                  <SelectContent>
                    {usersForSuggestion.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.user_name} - {user.user_email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* Suggestion type */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Tipo de sugerencia
              </label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant={suggestionType === "days" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSuggestionType("days")}
                  data-testid="type-days-btn"
                >
                  En X días
                </Button>
                <Button
                  type="button"
                  variant={suggestionType === "weeks" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSuggestionType("weeks")}
                  data-testid="type-weeks-btn"
                >
                  En X semanas
                </Button>
                <Button
                  type="button"
                  variant={suggestionType === "custom" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSuggestionType("custom")}
                  data-testid="type-custom-btn"
                >
                  Fecha específica
                </Button>
              </div>
            </div>
            
            {/* Value input for days/weeks */}
            {(suggestionType === "days" || suggestionType === "weeks") && (
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {suggestionType === "days" ? "Número de días" : "Número de semanas"}
                </label>
                <Input
                  type="number"
                  min="1"
                  max={suggestionType === "days" ? "60" : "12"}
                  value={suggestionValue}
                  onChange={(e) => setSuggestionValue(parseInt(e.target.value) || 1)}
                  data-testid="suggestion-value-input"
                />
                <p className="mt-1 text-xs text-stone-500">
                  Sesión sugerida para: {format(addDays(new Date(), suggestionType === "weeks" ? suggestionValue * 7 : suggestionValue), "EEEE d 'de' MMMM", { locale: es })}
                </p>
              </div>
            )}
            
            {/* Custom date/time picker */}
            {suggestionType === "custom" && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Fecha
                  </label>
                  <div className="flex justify-center">
                    <CalendarComponent
                      mode="single"
                      selected={customDate}
                      onSelect={setCustomDate}
                      disabled={(date) => date < addDays(new Date(), 1)}
                      locale={es}
                      className="rounded-lg border"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    Hora
                  </label>
                  <Select value={customTime} onValueChange={setCustomTime}>
                    <SelectTrigger data-testid="time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => {
                        const hour = i.toString().padStart(2, '0');
                        return [`${hour}:00`, `${hour}:30`];
                      }).flat().filter(t => {
                        const [h] = t.split(':').map(Number);
                        return h >= 7 && h <= 22;
                      }).map((time) => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Optional message */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Mensaje personalizado (opcional)
              </label>
              <Textarea
                value={suggestionMessage}
                onChange={(e) => setSuggestionMessage(e.target.value)}
                placeholder="Ej: Me gustaría continuar nuestra conversación sobre..."
                rows={3}
                data-testid="suggestion-message"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSuggestDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={sendSuggestion} 
                disabled={suggestLoading || !selectedUserId || (suggestionType === "custom" && (!customDate || !customTime))}
                className="btn-primary"
                data-testid="send-suggestion-btn"
              >
                {suggestLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar sugerencia
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </ListenerLayout>
  );
}
